import React, { useState, useEffect, useCallback } from 'react';
import CodeMirror from '@uiw/react-codemirror';
import { python } from '@codemirror/lang-python';
import { oneDark } from '@codemirror/theme-one-dark';
import { motion } from 'framer-motion';
import { 
  Play, 
  Trash, 
  Lightning,
  CircleNotch,
  CheckCircle,
  XCircle,
  BookOpen
} from '@phosphor-icons/react';

// Finance-focused practice exercises
const EXERCISES = {
  1: [
    {
      id: 'compound_interest',
      title: 'Compound Interest Calculator',
      description: 'Calculate the future value of an investment with compound interest.',
      difficulty: 'Easy',
      starterCode: `# Compound Interest Calculator
# Formula: FV = PV * (1 + r/n)^(n*t)

principal = 10000  # Initial investment
rate = 0.07        # 7% annual interest
compounds_per_year = 12  # Monthly compounding
years = 5          # 5 years

future_value = principal * (1 + rate/compounds_per_year) ** (compounds_per_year * years)

print("Compound Interest Calculator")
print("=" * 35)
print(f"Initial Investment: {principal:,.2f}")
print(f"Annual Rate: {rate*100}%")
print(f"Years: {years}")
print(f"Future Value: {future_value:,.2f}")
print(f"Interest Earned: {future_value - principal:,.2f}")`
    },
    {
      id: 'loan_payment',
      title: 'Loan Payment Calculator',
      description: 'Calculate monthly payments for a loan using the PMT formula.',
      difficulty: 'Medium',
      starterCode: `# Loan Payment Calculator (PMT Formula)

loan_amount = 250000  # Mortgage amount
annual_rate = 0.065   # 6.5% annual interest
years = 30            # 30-year mortgage

monthly_rate = annual_rate / 12
num_payments = years * 12

# PMT formula
monthly_payment = loan_amount * (monthly_rate * (1 + monthly_rate)**num_payments) / ((1 + monthly_rate)**num_payments - 1)

total_paid = monthly_payment * num_payments
total_interest = total_paid - loan_amount

print("Loan Payment Calculator")
print("=" * 35)
print(f"Loan Amount: {loan_amount:,}")
print(f"Interest Rate: {annual_rate*100}%")
print(f"Term: {years} years")
print(f"Monthly Payment: {monthly_payment:,.2f}")
print(f"Total Interest: {total_interest:,.2f}")`
    },
    {
      id: 'present_value',
      title: 'Present Value Calculator',
      description: 'Calculate the present value of a future cash flow.',
      difficulty: 'Easy',
      starterCode: `# Present Value Calculator
# PV = FV / (1 + r)^n

future_value = 50000  # Target amount
annual_rate = 0.06    # 6% expected return
years = 10            # In 10 years

present_value = future_value / (1 + annual_rate) ** years

print("Present Value Calculator")
print("=" * 35)
print(f"Future Goal: {future_value:,}")
print(f"Years: {years}")
print(f"Expected Return: {annual_rate*100}%")
print(f"Invest Today: {present_value:,.2f}")`
    }
  ],
  2: [
    {
      id: 'pandas_basics',
      title: 'Financial Data with Pandas',
      description: 'Create and analyze a simple financial dataset.',
      difficulty: 'Easy',
      starterCode: `import pandas as pd
import numpy as np

# Create sample financial data
data = {
    'Month': ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    'Revenue': [45000, 52000, 48000, 61000, 55000, 67000],
    'Expenses': [32000, 35000, 33000, 38000, 36000, 40000]
}

df = pd.DataFrame(data)
df['Profit'] = df['Revenue'] - df['Expenses']
df['Profit_Margin'] = (df['Profit'] / df['Revenue'] * 100).round(1)

print("Monthly Financial Summary")
print("=" * 50)
print(df.to_string(index=False))
print("=" * 50)
print(f"Total Revenue: {df['Revenue'].sum():,}")
print(f"Total Profit: {df['Profit'].sum():,}")
print(f"Avg Margin: {df['Profit_Margin'].mean():.1f}%")`
    },
    {
      id: 'variance_analysis',
      title: 'Budget vs Actual Variance',
      description: 'Analyze budget variances using Pandas.',
      difficulty: 'Medium',
      starterCode: `import pandas as pd

# Budget vs Actual Analysis
data = {
    'Category': ['Salaries', 'Marketing', 'Operations', 'R&D', 'Admin'],
    'Budget': [100000, 25000, 40000, 35000, 15000],
    'Actual': [98000, 32000, 38000, 42000, 14500]
}

df = pd.DataFrame(data)
df['Variance'] = df['Actual'] - df['Budget']
df['Variance_%'] = ((df['Variance'] / df['Budget']) * 100).round(1)

print("Budget vs Actual Variance Report")
print("=" * 55)
print(df.to_string(index=False))
print("=" * 55)
print(f"Total Budget: {df['Budget'].sum():,}")
print(f"Total Actual: {df['Actual'].sum():,}")
print(f"Net Variance: {df['Variance'].sum():,}")`
    }
  ],
  3: [
    {
      id: 'descriptive_stats',
      title: 'Financial Descriptive Statistics',
      description: 'Calculate key statistics for financial data.',
      difficulty: 'Easy',
      starterCode: `import numpy as np

# Monthly returns data (percentages)
monthly_returns = [2.3, -1.5, 4.2, 0.8, -2.1, 3.5, 1.2, -0.5, 2.8, 1.9, -1.2, 3.1]
returns = np.array(monthly_returns)

mean_return = np.mean(returns)
median_return = np.median(returns)
std_dev = np.std(returns)
variance = np.var(returns)

print("Portfolio Returns Analysis")
print("=" * 40)
print(f"Mean Return: {mean_return:.2f}%")
print(f"Median Return: {median_return:.2f}%")
print(f"Std Deviation: {std_dev:.2f}%")
print(f"Variance: {variance:.2f}")
print(f"Min Return: {np.min(returns):.2f}%")
print(f"Max Return: {np.max(returns):.2f}%")
print(f"Annualized Return: {mean_return * 12:.2f}%")
print(f"Annualized Vol: {std_dev * np.sqrt(12):.2f}%")`
    },
    {
      id: 'correlation',
      title: 'Correlation Analysis',
      description: 'Analyze correlation between financial variables.',
      difficulty: 'Medium',
      starterCode: `import numpy as np

# Marketing Spend vs Revenue (in thousands)
marketing = [10, 15, 12, 18, 20, 22, 25, 28, 30, 35]
revenue = [100, 120, 110, 140, 155, 160, 180, 195, 210, 240]

correlation = np.corrcoef(marketing, revenue)[0, 1]

# Simple linear regression
slope = np.cov(marketing, revenue)[0, 1] / np.var(marketing)
intercept = np.mean(revenue) - slope * np.mean(marketing)

print("Marketing vs Revenue Analysis")
print("=" * 40)
print(f"Correlation: {correlation:.3f}")

if correlation > 0.7:
    print("Interpretation: Strong positive")
elif correlation > 0.4:
    print("Interpretation: Moderate positive")

print(f"\\nRegression: Revenue = {slope:.2f} * Marketing + {intercept:.2f}")
print(f"Prediction: 30K spend -> {slope * 30 + intercept:.0f}K revenue")`
    }
  ],
  4: [
    {
      id: 'text_chart',
      title: 'ASCII Revenue Chart',
      description: 'Create a simple text-based visualization.',
      difficulty: 'Easy',
      starterCode: `# Simple ASCII Bar Chart
months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun']
revenue = [45, 52, 48, 61, 55, 67]  # in thousands

print("Monthly Revenue (in thousands)")
print("=" * 40)

max_rev = max(revenue)
for month, rev in zip(months, revenue):
    bar_length = int((rev / max_rev) * 30)
    bar = '#' * bar_length
    print(f"{month} | {bar} {rev}K")

print("=" * 40)
print(f"Average: {sum(revenue)/len(revenue):.1f}K")
print(f"Max: {max(revenue)}K")`
    }
  ],
  5: [
    {
      id: 'npv_calculator',
      title: 'NPV Calculator',
      description: 'Calculate Net Present Value for capital budgeting.',
      difficulty: 'Medium',
      starterCode: `import numpy as np

# Capital Budgeting: NPV Calculation
initial_investment = -500000  # Cash outflow
cash_flows = [120000, 150000, 180000, 200000, 220000]  # Years 1-5
discount_rate = 0.10  # 10% required return

all_flows = [initial_investment] + cash_flows
npv = sum(cf / (1 + discount_rate)**t for t, cf in enumerate(all_flows))

# Payback Period
cumulative = initial_investment
payback = 0
for i, cf in enumerate(cash_flows, 1):
    cumulative += cf
    if cumulative >= 0 and payback == 0:
        payback = i

print("Capital Budgeting Analysis")
print("=" * 45)
print(f"Initial Investment: {abs(initial_investment):,}")
print(f"Discount Rate: {discount_rate*100}%")
print("\\nProjected Cash Flows:")
for i, cf in enumerate(cash_flows, 1):
    pv = cf / (1 + discount_rate)**i
    print(f"  Year {i}: {cf:,} (PV: {pv:,.0f})")
print(f"\\nNPV: {npv:,.2f}")
print(f"Decision: {'ACCEPT' if npv > 0 else 'REJECT'}")
print(f"Payback: ~{payback} years")`
    },
    {
      id: 'dcf_model',
      title: 'Simple DCF Valuation',
      description: 'Discounted Cash Flow model for company valuation.',
      difficulty: 'Hard',
      starterCode: `import numpy as np

# DCF Valuation Model
current_fcf = 50_000_000  # Current FCF: 50M
growth_high = 0.15   # 15% growth years 1-5
growth_term = 0.03   # 3% terminal growth
wacc = 0.10          # 10% discount rate
shares = 10_000_000

# Project cash flows
projected = []
fcf = current_fcf
for year in range(1, 6):
    fcf = fcf * (1 + growth_high)
    projected.append(fcf)

# Terminal value
terminal_fcf = projected[-1] * (1 + growth_term)
terminal_value = terminal_fcf / (wacc - growth_term)

# Present values
pv_flows = sum(cf / (1 + wacc)**t for t, cf in enumerate(projected, 1))
pv_terminal = terminal_value / (1 + wacc)**5

enterprise_value = pv_flows + pv_terminal
per_share = enterprise_value / shares

print("DCF Valuation Model")
print("=" * 50)
print(f"Current FCF: {current_fcf/1e6:.1f}M")
print(f"Growth (1-5): {growth_high*100}%")
print(f"Terminal: {growth_term*100}%")
print(f"WACC: {wacc*100}%")
print("\\nProjected FCF:")
for i, fcf in enumerate(projected, 1):
    print(f"  Year {i}: {fcf/1e6:.1f}M")
print(f"\\nPV of Flows: {pv_flows/1e6:.1f}M")
print(f"Terminal Value: {terminal_value/1e6:.1f}M")
print(f"PV Terminal: {pv_terminal/1e6:.1f}M")
print(f"\\nEnterprise Value: {enterprise_value/1e6:.1f}M")
print(f"Per Share: {per_share:.2f}")`
    }
  ],
  6: [
    {
      id: 'simple_forecast',
      title: 'Revenue Forecast',
      description: 'Basic linear trend forecasting.',
      difficulty: 'Medium',
      starterCode: `import numpy as np

# Historical monthly revenue (12 months)
months = np.array([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12])
revenue = np.array([42, 45, 44, 48, 52, 51, 55, 58, 57, 62, 65, 68])

# Linear Regression
n = len(months)
slope = (n * np.sum(months * revenue) - np.sum(months) * np.sum(revenue)) / (n * np.sum(months**2) - np.sum(months)**2)
intercept = (np.sum(revenue) - slope * np.sum(months)) / n

# R-squared
predicted = slope * months + intercept
ss_res = np.sum((revenue - predicted)**2)
ss_tot = np.sum((revenue - np.mean(revenue))**2)
r_squared = 1 - (ss_res / ss_tot)

# Forecast next 3 months
forecast_months = [13, 14, 15]
forecasts = [slope * m + intercept for m in forecast_months]

print("Revenue Trend & Forecast")
print("=" * 45)
print(f"Equation: Revenue = {slope:.2f} * Month + {intercept:.2f}")
print(f"R-squared: {r_squared:.3f} ({r_squared*100:.1f}% explained)")
print(f"Monthly Growth: {slope:.2f}K/month")
print("\\nForecast:")
for m, f in zip(forecast_months, forecasts):
    print(f"  Month {m}: {f:.1f}K")
print(f"\\nQ1 Total: {sum(forecasts):.1f}K")`
    }
  ]
};

const CodePlayground = ({ currentStage, onAskTutor }) => {
  const [pyodide, setPyodide] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [output, setOutput] = useState('');
  const [error, setError] = useState('');
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [code, setCode] = useState('# Write your Python code here\nprint("Hello, Finance!")');
  const [exerciseStage, setExerciseStage] = useState(currentStage || 1);

  useEffect(() => {
    const loadPyodide = async () => {
      try {
        setLoading(true);
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/pyodide/v0.24.1/full/pyodide.js';
        script.async = true;
        
        script.onload = async () => {
          try {
            const pyodideInstance = await window.loadPyodide({
              indexURL: 'https://cdn.jsdelivr.net/pyodide/v0.24.1/full/'
            });
            await pyodideInstance.loadPackage(['numpy', 'pandas']);
            setPyodide(pyodideInstance);
            setLoading(false);
            setOutput('Python ready! NumPy + Pandas loaded.\n\nSelect an exercise or write your own code.');
          } catch (err) {
            setError('Failed to initialize Python. Refresh the page.');
            setLoading(false);
          }
        };
        
        document.head.appendChild(script);
      } catch (err) {
        setError('Failed to load Python.');
        setLoading(false);
      }
    };

    if (!window.loadPyodide) {
      loadPyodide();
    } else {
      const initPyodide = async () => {
        try {
          const pyodideInstance = await window.loadPyodide({
            indexURL: 'https://cdn.jsdelivr.net/pyodide/v0.24.1/full/'
          });
          await pyodideInstance.loadPackage(['numpy', 'pandas']);
          setPyodide(pyodideInstance);
          setLoading(false);
          setOutput('Python ready!');
        } catch (err) {
          setError('Failed to initialize Python.');
          setLoading(false);
        }
      };
      initPyodide();
    }
  }, []);

  const runCode = useCallback(async () => {
    if (!pyodide || running) return;
    
    setRunning(true);
    setOutput('');
    setError('');
    
    try {
      pyodide.runPython(`
import sys
from io import StringIO
sys.stdout = StringIO()
      `);
      
      await pyodide.runPythonAsync(code);
      const stdout = pyodide.runPython('sys.stdout.getvalue()');
      setOutput(stdout || '(No output)');
    } catch (err) {
      setError(err.message);
    } finally {
      setRunning(false);
    }
  }, [pyodide, code, running]);

  const loadExercise = (exercise) => {
    setSelectedExercise(exercise);
    setCode(exercise.starterCode);
    setOutput('');
    setError('');
  };

  const exercises = EXERCISES[exerciseStage] || [];

  return (
    <div className="bg-zinc-900/50 border border-white/10 rounded-lg overflow-hidden">
      <div className="p-4 border-b border-white/10 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
            <Lightning size={20} className="text-green-400" weight="duotone" />
          </div>
          <div>
            <h3 className="font-outfit font-semibold text-white">Python Practice</h3>
            <p className="text-xs text-zinc-400">
              {loading ? 'Loading Python...' : 'Pyodide + NumPy + Pandas'}
            </p>
          </div>
        </div>
        
        <select
          data-testid="exercise-stage-select"
          value={exerciseStage}
          onChange={(e) => setExerciseStage(Number(e.target.value))}
          className="bg-zinc-800 border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-orange-500/50"
        >
          <option value={1}>Stage 1: Python Basics</option>
          <option value={2}>Stage 2: Pandas</option>
          <option value={3}>Stage 3: Statistics</option>
          <option value={4}>Stage 4: Visualization</option>
          <option value={5}>Stage 5: Financial Modeling</option>
          <option value={6}>Stage 6: ML Forecasting</option>
        </select>
      </div>

      <div className="flex flex-col lg:flex-row">
        <div className="lg:w-64 border-b lg:border-b-0 lg:border-r border-white/10 p-4">
          <h4 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-3">
            Exercises
          </h4>
          <div className="space-y-2">
            {exercises.map((exercise) => (
              <button
                key={exercise.id}
                data-testid={`exercise-${exercise.id}`}
                onClick={() => loadExercise(exercise)}
                className={`w-full text-left p-3 rounded-lg transition-colors ${
                  selectedExercise?.id === exercise.id
                    ? 'bg-orange-500/20 border border-orange-500/30'
                    : 'bg-zinc-800/50 border border-transparent hover:border-white/10'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-white">{exercise.title}</span>
                  <span className={`text-xs px-2 py-0.5 rounded ${
                    exercise.difficulty === 'Easy' ? 'bg-green-500/20 text-green-400' :
                    exercise.difficulty === 'Medium' ? 'bg-yellow-500/20 text-yellow-400' :
                    'bg-red-500/20 text-red-400'
                  }`}>
                    {exercise.difficulty}
                  </span>
                </div>
                <p className="text-xs text-zinc-400 mt-1 line-clamp-2">{exercise.description}</p>
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 flex flex-col">
          <div className="p-3 border-b border-white/10 flex items-center gap-2">
            <button
              data-testid="run-code-btn"
              onClick={runCode}
              disabled={loading || running}
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              {running ? (
                <>
                  <CircleNotch size={16} className="animate-spin" />
                  Running...
                </>
              ) : (
                <>
                  <Play size={16} weight="fill" />
                  Run Code
                </>
              )}
            </button>
            
            <button
              data-testid="clear-code-btn"
              onClick={() => {
                setCode('# Write your Python code here\n');
                setOutput('');
                setError('');
                setSelectedExercise(null);
              }}
              className="flex items-center gap-2 bg-zinc-700 hover:bg-zinc-600 text-white px-3 py-2 rounded-lg text-sm transition-colors"
            >
              <Trash size={16} />
              Clear
            </button>

            {selectedExercise && onAskTutor && (
              <button
                data-testid="ask-tutor-help-btn"
                onClick={() => onAskTutor(`Help me understand: ${selectedExercise.title}. ${selectedExercise.description}`)}
                className="flex items-center gap-2 bg-orange-500/20 hover:bg-orange-500/30 text-orange-400 px-3 py-2 rounded-lg text-sm transition-colors ml-auto"
              >
                <BookOpen size={16} />
                Ask Tutor
              </button>
            )}
          </div>

          <div className="flex-1 min-h-[300px]">
            {loading ? (
              <div className="h-full flex items-center justify-center">
                <div className="text-center">
                  <CircleNotch size={32} className="text-orange-400 animate-spin mx-auto mb-3" />
                  <p className="text-zinc-400 text-sm">Loading Python environment...</p>
                  <p className="text-zinc-500 text-xs mt-1">This may take a few seconds</p>
                </div>
              </div>
            ) : (
              <CodeMirror
                value={code}
                height="300px"
                theme={oneDark}
                extensions={[python()]}
                onChange={(value) => setCode(value)}
                className="text-sm"
                data-testid="code-editor"
              />
            )}
          </div>

          <div className="border-t border-white/10">
            <div className="p-2 bg-zinc-800/50 flex items-center gap-2">
              <span className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">Output</span>
              {output && !error && <CheckCircle size={14} className="text-green-400" />}
              {error && <XCircle size={14} className="text-red-400" />}
            </div>
            <pre 
              data-testid="code-output"
              className={`p-4 text-sm font-mono min-h-[120px] max-h-[200px] overflow-auto ${
                error ? 'text-red-400' : 'text-zinc-300'
              }`}
            >
              {error || output || 'Output will appear here...'}
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CodePlayground;
